/**
 * Blended Cost composable for ASAB Models
 * Calculates the weighted average cost per million tokens based on routing targets
 * Formula: Σ(weight_i × cost_i) / Σ(weight_i) for both input and output costs
 */

import { computed } from 'vue';
import type { Ref } from 'vue';
import type { RoutingTarget, BaseModelResponse, CustomModelResponse, BlendedCost } from '../types';

/**
 * Composable for calculating blended cost for ASAB models
 * Takes routing targets and available models, returns reactive computed blended cost
 *
 * @param routingTargets - Reactive reference to array of routing targets with weights
 * @param baseModels - Reactive reference to array of available base models
 * @param customModels - Reactive reference to array of available custom models
 * @returns Object containing reactive blendedCost computed property
 *
 * @example
 * ```typescript
 * const routingTargets = ref<RoutingTarget[]>([
 *   { backend_id: 'gpt-4', backend_type: 'base', weight: 70 },
 *   { backend_id: 'claude-3', backend_type: 'base', weight: 30 }
 * ]);
 * const baseModels = ref<BaseModelResponse[]>([...]);
 * const customModels = ref<CustomModelResponse[]>([]);
 *
 * const { blendedCost } = useBlendedCost(routingTargets, baseModels, customModels);
 * // blendedCost.value => { input_1m: 15.5, output_1m: 45.2 }
 * ```
 */
export function useBlendedCost(
  routingTargets: Ref<RoutingTarget[]>,
  baseModels: Ref<BaseModelResponse[]>,
  customModels: Ref<CustomModelResponse[]>
) {
  const matchesModel = (
    backendId: string,
    model: BaseModelResponse | CustomModelResponse
  ): boolean => {
    const normalizedBackend = backendId.trim().toLowerCase();
    const nameMatch = model.name.trim().toLowerCase() === normalizedBackend;
    const slugMatch = model.slug.trim().toLowerCase() === normalizedBackend;
    return nameMatch || slugMatch;
  };
  /**
   * Computed property that calculates blended cost based on routing targets and their weights
   * Returns { input_1m: 0, output_1m: 0 } if no routing targets or total weight is 0
   * Ignores routing targets that reference non-existent models
   */
  const blendedCost = computed<BlendedCost>(() => {
    // Return zero cost if no routing targets
    if (routingTargets.value.length === 0) {
      return { input_1m: 0, output_1m: 0 };
    }

    // Calculate total weight
    const totalWeight = routingTargets.value.reduce((sum, target) => sum + target.weight, 0);

    // Return zero cost if total weight is 0
    if (totalWeight === 0) {
      return { input_1m: 0, output_1m: 0 };
    }

    // Calculate weighted costs
    let weightedInputCost = 0;
    let weightedOutputCost = 0;

    for (const target of routingTargets.value) {
      // Find the model based on backend_type
      // Note: backend_id is the model's name (not slug) as per API spec
      let model: BaseModelResponse | CustomModelResponse | undefined;

      if (target.backend_type === 'base') {
        model = baseModels.value.find(m => matchesModel(target.backend_id, m));
      } else if (target.backend_type === 'custom') {
        model = customModels.value.find(m => matchesModel(target.backend_id, m));
      }

      // If model found, add weighted costs
      if (model) {
        // For custom models, costs might be null, treat as 0
        const inputCost = model.input_cost_1m ?? 0;
        const outputCost = model.output_cost_1m ?? 0;

        weightedInputCost += target.weight * inputCost;
        weightedOutputCost += target.weight * outputCost;
      }
    }

    // Calculate blended cost: Σ(weight_i × cost_i) / Σ(weight_i)
    return {
      input_1m: weightedInputCost / totalWeight,
      output_1m: weightedOutputCost / totalWeight,
    };
  });

  return {
    blendedCost,
  };
}
